import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Star, MessageCircle, User, Plus } from 'lucide-react';
import axios from 'axios';

const ProductReviews = ({ product, onReviewAdded }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newReview, setNewReview] = useState({
    rating: 0,
    comment: ''
  });
  const { API, user } = useContext(AuthContext);

  useEffect(() => {
    fetchReviews();
  }, [product.id]);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/products/${product.id}/reviews`);
      setReviews(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast.error('Você precisa estar logado para avaliar');
      return;
    }

    if (newReview.rating === 0) {
      toast.error('Por favor, selecione uma avaliação');
      return;
    }

    try {
      await axios.post(`${API}/products/${product.id}/reviews`, {
        product_id: product.id,
        rating: newReview.rating,
        comment: newReview.comment
      });

      toast.success('Avaliação enviada com sucesso!');
      setIsDialogOpen(false);
      setNewReview({ rating: 0, comment: '' });
      fetchReviews();
      
      // Callback to update product rating in parent component
      if (onReviewAdded) {
        onReviewAdded();
      }
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error(error.response?.data?.detail || 'Erro ao enviar avaliação');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const renderStars = (rating, interactive = false, onRatingChange = null) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-5 h-5 ${
              star <= rating
                ? 'text-yellow-400 fill-current'
                : 'text-gray-300'
            } ${interactive ? 'cursor-pointer hover:text-yellow-400' : ''}`}
            onClick={() => interactive && onRatingChange && onRatingChange(star)}
          />
        ))}
        {!interactive && (
          <span className="ml-2 text-sm text-gray-600">
            ({rating.toFixed(1)})
          </span>
        )}
      </div>
    );
  };

  const canReview = user && user.user_type === 'consumidor' && 
                   !reviews.some(review => review.user_id === user.id);

  return (
    <div className="space-y-4">
      {/* Rating Summary */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h3 className="text-lg font-semibold">Avaliações</h3>
          {product.total_reviews > 0 && (
            <div className="flex items-center gap-2">
              {renderStars(product.average_rating || 0)}
              <span className="text-sm text-gray-600">
                {product.total_reviews} avaliação{product.total_reviews !== 1 ? 'ões' : ''}
              </span>
            </div>
          )}
        </div>

        {canReview && (
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-feira flex items-center gap-2" data-testid="add-review-button">
                <Plus className="w-4 h-4" />
                Avaliar
              </Button>
            </DialogTrigger>
            
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Avaliar Produto</DialogTitle>
              </DialogHeader>
              
              <form onSubmit={handleSubmitReview} className="space-y-4">
                <div>
                  <Label>Sua avaliação</Label>
                  <div className="mt-2">
                    {renderStars(newReview.rating, true, (rating) => 
                      setNewReview(prev => ({ ...prev, rating }))
                    )}
                  </div>
                </div>

                <div>
                  <Label htmlFor="comment">Comentário (opcional)</Label>
                  <Textarea
                    id="comment"
                    placeholder="Conte sua experiência com este produto..."
                    value={newReview.comment}
                    onChange={(e) => setNewReview(prev => ({ ...prev, comment: e.target.value }))}
                    rows={3}
                    className="mt-2"
                  />
                </div>

                <div className="flex gap-2">
                  <Button type="submit" className="btn-feira flex-1">
                    Enviar Avaliação
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setIsDialogOpen(false)}
                  >
                    Cancelar
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Reviews List */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="border rounded-lg p-4">
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-8 h-8 bg-gray-200 rounded-full skeleton"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-1/4 skeleton"></div>
                </div>
              </div>
              <div className="h-4 bg-gray-200 rounded w-full skeleton"></div>
            </div>
          ))}
        </div>
      ) : reviews.length === 0 ? (
        <div className="text-center py-8 border rounded-lg" data-testid="no-reviews-message">
          <MessageCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 mb-2">Nenhuma avaliação ainda</p>
          <p className="text-sm text-gray-400">Seja o primeiro a avaliar este produto!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {reviews.map((review) => (
            <Card key={review.id} className="border" data-testid={`review-${review.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-gradient-to-br from-green-400 to-orange-400 rounded-full flex items-center justify-center text-white font-bold text-sm">
                      {review.user_name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">{review.user_name}</p>
                      <div className="flex items-center gap-2">
                        {renderStars(review.rating)}
                        <span className="text-sm text-gray-500">
                          {formatDate(review.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {review.comment && (
                  <p className="text-gray-700 mt-2">{review.comment}</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductReviews;